<template>
  <b-form @submit.prevent="submitForm">
    <!-- Serial Number -->
    <div class="form-container">
      <div class="form-section">
        <h5 class="section-title">
          {{ $t('deviceinfo') }}
        </h5>
        <div class="form-row">
          <b-form-group :label="$t('sndevice')">
            <b-form-input
              v-model="form.serialNumber"
              maxlength="50"
              class="input-box"
              required
            />
          </b-form-group>

          <!-- Category -->
          <b-form-group :label="$t('catedevice')">
            <b-form-select
              v-model="form.categoryId"
              :options="categoriesOptions"
              class="input-box"
              :select-size="4"
              required
              @change="handleCategoryChange"
            />
          </b-form-group>

          <!-- SubCategory -->
          <b-form-group :label="$t('subcatedevice')">
            <b-form-select
              v-model="form.subcategoryId"
              :options="subcategoriesOptions"
              :select-size="4"
              :disabled="isLoading.SubCate || !form.categoryId"
              class="input-box"
              required
            />
            <b-spinner
              v-if="isLoading.SubCate"
              variant="border"
              small
            />
          </b-form-group>
        </div>

        <div class="form-row">
          <!-- Device Name -->
          <b-form-group :label="$t('devicename')">
            <b-form-input
              v-model="form.deviceName"
              maxlength="50"
              :placeholder="form.categoryId ? $t('enterdevice') : $t('entersubdevice')"
              required
            />
          </b-form-group>

          <!-- Model -->
          <b-form-group :label="$t('model')">
            <b-form-input
              v-model="form.model"
              maxlength="50"
            />
          </b-form-group>
          <!-- Price -->
          <b-form-group :label="$t('cost')">
            <b-form-input
              v-model="form.price"
              type="number"
              required
              step="0.01"
              @keydown="limitInputcost"
            />
          </b-form-group>
        </div>
      </div>

      <div
        class="form-section"
      > <h5 class="section-title">
          {{ $t('userinfo') }}
        </h5>
        <div class="form-row">
          <!-- User -->
          <b-form-group :label="$t('user')">
            <b-form-select
              v-model="form.userId"
              :options="usersOptions"
              class="input-box"
              :select-size="4"
              required
              @change="onUserChanged"
            />
          </b-form-group>

          <!-- Department -->
          <b-form-group :label="$t('deptname')">
            <b-form-select
              v-model="form.departmentId"
              :options="departmentsOptions"
              class="input-box"
              :select-size="4"
              required
            />
          </b-form-group>

          <!-- Location -->
          <b-form-group :label="$t('locationname')">
            <b-form-select
              v-model="form.locationId"
              :options="locationsOptions"
              class="input-box"
              :select-size="4"
              required
            />
          </b-form-group>

        </div>
        <!-- Purchase Date -->
        <b-form-group :label="$t('getdevice')">
          <b-form-input
            v-model="form.purchaseDate"
            class="input-box"
            type="date"
          />
        </b-form-group>
      </div>
    </div>

    <div class="form-container">
      <!-- warranty -->
      <div
        v-if="hasDeviceId"
        class="form-section"
      >
        <h5 class="section-title">
          {{ $t('warrantydetails') }}
        </h5>
        <b-button @click="showwarrantyModal = true">
          {{ $t('addlist') }}
        </b-button>

        <div
          v-if="isLoading.warranty"
          class="text-center"
        >
          <b-spinner label="Loading..." />
        </div>

        <div v-else>
          <b-modal
            v-model="showwarrantyModal"
            :title="$t('warrantydetail')"
          >
            <h5 class="section-title">
              {{ $t('warrantydetail') }}
            </h5>
            <b-form-group :label="$t('providerName')">
              <b-form-input
                v-model="warranty.vendor"
                maxlength="50"
                required
              />
            </b-form-group>

            <b-form-group :label="$t('warrantytime')">
              <div class="d-flex">
                <b-form-select
                  v-model="warranty.warrantyYear"
                  :options="yearOptions"
                  required
                  @change="updateDisplay"
                />
                <b-form-select
                  v-model="warranty.warrantyMonth"
                  :options="monthOptions"
                  required
                  @change="updateDisplay"
                />
                <b-form-select
                  v-model="warranty.warrantyDay"
                  :options="dayOptions"
                  required
                  @change="updateDisplay"
                />
              </div>
              <div v-if="displayWarranty">
                <span>{{ displayWarranty }}</span>
              </div>
            </b-form-group>

            <b-form-group :label="$t('expdate')">
              <b-form-datepicker
                v-model="warranty.warrantyExp"
                required
              />
            </b-form-group>

            <b-form-group :label="$t('remark')">
              <b-form-textarea
                v-model="warranty.warrantyNote"
                :placeholder="$t('remarkinput')"
                rows="3"
                maxlength="100"
                @input="checkwarrantyLength"
              />
              <small class="form-text text-muted">{{ warranty.warrantyNote.length }}/100 {{ $t('text') }}</small>
              <b-alert
                v-if="lengthwarrantyExceeded"
                variant="danger"
                show
              >
                {{ $t('text100') }}
              </b-alert>
            </b-form-group>

            <template #modal-footer>
              <b-button
                variant="secondary"
                @click="showwarrantyModal = false"
              >
                {{ $t('cancel') }}
              </b-button>
              <b-button
                variant="primary"
                @click="saveWarranty"
              >
                {{ $t('confirm') }}
              </b-button>
            </template>
          </b-modal>
        </div>
        <!-- warranty form -->
        <div
          style="max-height: 200px; overflow-y: auto;"
        >
          <b-table
            :items="warrantyList"
            :fields="warrantyFields"
            class="mt-3"
            bordered
          >
            <template #empty>
              <div>{{ $t('nodatawarranty') }}</div>
            </template>
            <template #cell(vendor)="data">
              <span>{{ data.item.vendor }}</span>
            </template>
            <template #cell(warrantyDay)="data">
              <span>
                {{ data.item.warrantyDay && data.item.warrantyDay > 0 ? formatWarrantyDay(data.item.warrantyDay) : $t('nowarranty') }}
              </span>
            </template>
            <template #cell(warrantyExp)="data">
              <span
                v-if="data.item.warrantyExp !== null"
                :style="{ color: formatWarrantyExp(data.item.warrantyExp).color }"
              >
                {{ formatWarrantyExp(data.item.warrantyExp).text }}
              </span>
              <span v-else />
            </template>
            <template #cell(warrantyNote)="data">
              <span>{{ data.item.warrantyNote }}</span>
            </template>
            <template #cell(actions)="data">
              <b-button
                size="sm"
                variant="warning"
                class="btn-space"
                @click="editWarranty(data.item)"
              >
                <b-icon
                  icon="pencil"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                size="sm"
                variant="danger"
                class="btn-space"
                @click="deleteWarrantyModel(data.item.id)"
              >
                <b-icon
                  icon="trash"
                  aria-hidden="true"
                />
              </b-button>
            </template>
          </b-table>
        </div>
      </div>

      <!--devicehardware-->
      <div
        v-if="hasDeviceId"
        class="form-section"
      >
        <h5 class="section-title">
          {{ $t('hardwaredetails') }}
        </h5>
        <b-button @click="showDeviceModal = true">
          {{ $t('addlist') }}
        </b-button>

        <b-modal
          v-model="showDeviceModal"
          :title="$t('hardwaredetails')"
        >
          <h5 class="section-title">
            {{ $t('hardwaredetails') }}
          </h5>
          <b-form-group label="CPU">
            <b-form-input
              v-model="deviceHardware.cpu"
              maxlength="50"
            />
          </b-form-group>

          <b-form-group label="RAM">
            <b-form-input
              v-model="deviceHardware.ram"
              maxlength="50"
            />
          </b-form-group>

          <b-form-group label="Display">
            <b-form-input
              v-model="deviceHardware.display"
              maxlength="50"
            />
          </b-form-group>

          <b-form-group label="Hard Disk">
            <b-form-input
              v-model="deviceHardware.harddisk"
              maxlength="50"
            />
          </b-form-group>

          <b-form-group label="SSD">
            <b-form-input
              v-model="deviceHardware.ssd"
              maxlength="50"
            />
          </b-form-group>

          <b-form-group label="IP Address">
            <b-form-input
              v-model="deviceHardware.ip"
              maxlength="50"
            />
          </b-form-group>

          <b-form-group label="MAC Address">
            <b-form-input
              v-model="deviceHardware.macAdd"
              maxlength="50"
            />
          </b-form-group>

          <b-form-group label="Keyboard">
            <b-form-input
              v-model="deviceHardware.keyboard"
              maxlength="50"
            />
          </b-form-group>

          <b-form-group label="Mouse">
            <b-form-input
              v-model="deviceHardware.mouse"
              maxlength="50"
            />
          </b-form-group>

          <b-form-group label="UPS">
            <b-form-input
              v-model="deviceHardware.ups"
              maxlength="50"
            />
          </b-form-group>

          <b-form-group :label="$t('details')">
            <b-form-textarea
              v-model="deviceHardware.details"
              :placeholder="$t('detailsinput')"
              rows="3"
              maxlength="100"
              @input="checkdevicehdLength"
            />
            <small class="form-text text-muted">{{ deviceHardware.details.length }}/100 {{ $t('text') }}</small>
            <b-alert
              v-if="lengthdevicehdExceeded"
              variant="danger"
              show
            >
              {{ $t('text100') }}
            </b-alert>
          </b-form-group>

          <template #modal-footer>
            <b-button
              variant="secondary"
              @click="showDeviceModal = false"
            >
              {{ $t('cancel') }}
            </b-button>
            <b-button
              variant="primary"
              @click="saveDeviceHardware"
            >
              {{ $t('confirm') }}
            </b-button>
          </template>
        </b-modal>

        <!-- Device Hardware Table -->
        <div style="max-height: 200px; overflow-y: auto;">
          <b-table
            :items="deviceHardwareList"
            :fields="deviceHardwareFields"
            class="mt-3"
            bordered
          >
            <template #empty>
              <div>{{ $t('nodetailshardware') }}</div>
            </template>
            <template #cell(cpu)="data">
              <span>{{ data.item.cpu }}</span>
            </template>
            <template #cell(ram)="data">
              <span>{{ data.item.ram }}</span>
            </template>
            <template #cell(display)="data">
              <span>{{ data.item.display }}</span>
            </template>
            <template #cell(harddisk)="data">
              <span>{{ data.item.harddisk }}</span>
            </template>
            <template #cell(ssd)="data">
              <span>{{ data.item.ssd }}</span>
            </template>
            <template #cell(ip)="data">
              <span>{{ data.item.ip }}</span>
            </template>
            <template #cell(macAdd)="data">
              <span>{{ data.item.macAdd }}</span>
            </template>
            <template #cell(details)="data">
              <span>{{ data.item.details }}</span>
            </template>
            <template #cell(actions)="data">
              <b-button
                size="sm"
                variant="warning"
                class="btn-space"
                @click="editDeviceHardware(data.item)"
              >
                <b-icon
                  icon="pencil"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                size="sm"
                variant="danger"
                class="btn-space"
                @click="deleteDeviceHardwareModel(data.item.id)"
              >
                <b-icon
                  icon="trash"
                  aria-hidden="true"
                />
              </b-button>
            </template>
          </b-table>
        </div>
      </div>
    </div>

    <div class="form-container">
      <div
        class="form-section"
      > <h5 class="section-title">
          {{ $t('devicestatus') }}
        </h5>
        <div class="form-row">
          <!-- purchaseStatus Dropdown -->
          <b-form-group :label="$t('assetType')">
            <b-form-select
              v-model="form.purchaseStatus"
              :options="purchaseStatusOptions"
              class="input-box"
              required
              @change="handleStatusChange"
            />
          </b-form-group>

          <!-- Status Dropdown -->
          <b-form-group :label="$t('devicestatus')">
            <b-form-select
              v-model="form.status"
              :options="statusOptions"
              class="input-box"
              required
              @change="handleStatusChange"
            />
          </b-form-group>

          <!--date Status dis-->
          <b-form-group
            v-if="isInactive"
            :label="$t('endDate')"
            class="input-time"
          >
            <b-form-datepicker
              v-model="form.inactiveDate"
              required
            />
          </b-form-group>
        </div>
      </div>

      <div
        class="form-section"
      ><h5 class="section-title">
         {{ $t('otherdetails') }}
       </h5>
        <!--details-->
        <b-form-group :label="$t('detailsanother')">
          <b-form-textarea
            v-model="form.details"
            :placeholder="$t('detailsanotherinput')"
            rows="3"
            class="input-text"
            maxlength="150"
            @input="checkLength"
          />
          <small class="form-text text-muted">{{ form.details.length }}/150 {{ $t('text') }}</small>
          <b-alert
            v-if="lengthExceeded"
            variant="danger"
            show
          >
            {{ $t('text150') }}
          </b-alert>
        </b-form-group>
      </div>
    </div>

    <!-- เพื่มรูปภาพ image device -->
    <b-form-group :label="$t('addimage')">
      <b-form-file
        multiple
        class="input-image"
        accept="image/*"
        :placeholder="$t('selectimage')"
        @change="handleFileChange"
      />
    </b-form-group>

    <div
      v-if="device && device.id"
    >
      <b-form-group :label="$t('image')">
        <div v-if="isLoading.Images">
          <!-- แสดง spinner ขณะกำลังโหลด -->
          <b-spinner :label="$t('loading')" />
        </div>
        <div v-else>
          <!-- แสดงรูปภาพเมื่อโหลดเสร็จ -->
          <div v-if="imageUrls.length > 0">
            <b-img
              v-for="(url, index) in imageUrls"
              :key="index"
              :src="url"
              alt="Uploaded Image"
              class="img-thumbnail mb-2"
              fluid
              @click="showImage(url)"
            />
          </div>
          <div v-else>
            <!-- แสดงข้อความหากไม่มีรูปภาพ -->
            {{ $t('noimage') }}
          </div>
        </div>
      </b-form-group>
    </div>

    <!-- Modal สำหรับกรอก note -->
    <b-modal
      v-model="showDeviceHistoryModal"
      :title="$t('edituserdevice')"
      :ok-title="$t('yes')"
      :cancel-title="$t('cancel')"
      @ok="handleModalOk"
    >
      <b-form-group :label="$t('remark')">
        <b-form-textarea
          v-model="history.note"
          :placeholder="$t('remarkinput')"
          rows="3"
          class="input-texthistory"
          required
          :max-length="30"
          @input="checkhistoryDetailsLength"
        />
        <small class="form-text text-muted">{{ history.note.length }}/30 {{ $t('text') }}</small>
        <b-alert
          v-if="lengthhistoryDetailsExceeded"
          variant="danger"
          show
        >
          {{ $t('text30') }}
        </b-alert>
      </b-form-group>
    </b-modal>
    <!-- Image Modal -->
    <b-modal
      v-model="imageModalShow"
      size="lg"
      :title="$t('showimage')"
      hide-footer
      @hidden="handleImageClose"
    >
      <b-img
        :src="selectedImage"
        alt="Large Image"
        fluid
      />
    </b-modal>

    <!-- Submit Button -->
    <div class="form-buttons">
      <b-button
        variant="secondary"
        @click="$emit('close')"
      >
        {{ $t('cancel') }}
      </b-button>
      <b-button
        type="submit"
        variant="primary"
      >
        {{ $t('submit') }}
      </b-button>
    </div>

    <!--model ลบของ warranty-->
    <b-modal
      v-model="showConfirmationModalwarranty"
      :title="$t('delconfirm')"
      :ok-title="$t('yes')"
      :cancel-title="$t('cancel')"
      @ok="deleteWarranty"
    >
      <p class="my-4">
        {{ $t('confirmdelete') }}
      </p>
    </b-modal>

    <!--model ลบของ hardware-->
    <b-modal
      v-model="showConfirmationModalhardware"
      :title="$t('delconfirm')"
      :ok-title="$t('yes')"
      :cancel-title="$t('cancel')"
      @ok="deleteDeviceHardware"
    >
      <p class="my-4">
        {{ $t('confirmdelete') }}
      </p>
    </b-modal>
  </b-form>
</template>

<script>
import apiAdmin from '@/services/admin'
import apiMst from '@/services/masterDate'

export default {
  props: {
    device: Object,
    categories: Array,
    users: Array,
    locations: Array,
    departments: Array,
  },
  data() {
    return {
      form: {
        deviceId: null,
        serialNumber: '',
        deviceName: '',
        model: '',
        categoryId: null,
        subcategoryId: null,
        userId: null,
        purchaseDate: '',
        departmentId: null,
        locationId: null,
        price: 0,
        status: '',
        purchaseStatus: '',
        inactiveDate: null,
        details: '',
      },
      history: {
        note: '',
      },
      showDeviceHistoryModal: false,
      subcategoriesOptions: [],
      categoriesOptions: [],
      usersOptions: [],
      locationsOptions: [],
      departmentsOptions: [],
      warranty: {
        vendor: '',
        warrantyYear: 0,
        warrantyMonth: 0,
        warrantyDay: 0,
        warrantyExp: null,
        warrantyNote: '',
      },
      warrantyList: [],
      displayWarranty: '',
      deviceHardware: {
        id: null,
        cpu: '',
        ram: '',
        display: '',
        harddisk: '',
        ssd: '',
        ip: '',
        macAdd: '',
        keyboard: '',
        mouse: '',
        ups: '',
        details: '',
      },
      deviceHardwareList: [],
      showwarrantyModal: false,
      selectedFiles: [],
      imageUrls: [],
      imageModalShow: false,
      selectedImage: null,
      lengthExceeded: false,
      lengthdevicehdExceeded: false,
      lengthwarrantyExceeded: false,
      showDeviceModal: false,
      showDeviceSection: false,
      lengthhistoryDetailsExceeded: false,
      warrantyIdToDelete: null,
      showConfirmationModalwarranty: false,
      hardwareIdToDelete: null,
      showConfirmationModalhardware: false,
      isLoading: {
        warranty: false,
        SubCate: false,
        Images: false,
      },
      yearOptions: [
        { value: 0, text: this.$t('zeroyear') },
        ...Array.from({ length: 99 }, (_, i) => ({ value: i + 1, text: `${(i + 1).toString()} ${this.$t('year')}` })),
      ],
      monthOptions: [
        { value: 0, text: this.$t('zeromonth') },
        ...Array.from({ length: 12 }, (_, i) => ({ value: i + 1, text: `${(i + 1).toString()} ${this.$t('month')}` })),
      ],
      dayOptions: [
        { value: 0, text: this.$t('zeroday') },
        ...Array.from({ length: 31 }, (_, i) => ({ value: i + 1, text: `${(i + 1).toString()} ${this.$t('day')}` })),
      ],

      statusOptions: [
        { value: 'ใช้งาน', text: this.$t('use') },
        { value: 'เลิกใช้งาน', text: this.$t('nonuse') },
        { value: 'ขายเศษซาก', text: this.$t('sellscrap') },
      ],
      purchaseStatusOptions: [
        { value: 'เช่าใช้งาน', text: this.$t('rent') },
        { value: 'ซื้อขาด', text: this.$t('buy') },
      ],
    }
  },
  computed: {
    isInactive() {
      return this.form.status === 'เลิกใช้งาน' || this.form.status === 'ขายเศษซาก'
    },
    hasDeviceId() {
      console.log('Device ID computed:', this.form.deviceId)
      return this.form.deviceId !== null && this.form.deviceId !== ''
    },
    deviceHardwareFields() {
      return [
        { key: 'cpu', label: 'CPU' },
        { key: 'ram', label: 'RAM' },
        { key: 'display', label: 'Display' },
        { key: 'harddisk', label: 'Hard Disk' },
        { key: 'ssd', label: 'SSD' },
        { key: 'ip', label: 'IP Address' },
        { key: 'macAdd', label: 'MAC Address' },
        { key: 'mouse', label: 'Mouse' },
        { key: 'keyboard', label: 'Keyboard' },
        { key: 'ups', label: 'UPS' },
        { key: 'details', label: this.$t('datailsanother') },
        { key: 'actions', label: 'Actions', sortable: false },
      ]
    },
    warrantyFields() {
      return [
        { key: 'vendor', label: this.$t('vendor') },
        { key: 'warrantyDay', label: this.$t('warrantyday') },
        { key: 'warrantyExp', label: this.$t('warrantyExp') },
        { key: 'warrantyNote', label: this.$t('remark') },
        { key: 'actions', label: this.$t('management'), sortable: false },
      ]
    },
  },
  watch: {
    'form.categoryId': function (newCategoryId) {
      if (newCategoryId) {
        this.fetchSubcategories()
      } else {
        this.subcategoriesOptions = []
      }
    },
    device: {
      immediate: true,
      handler(newDevice) {
        if (newDevice) {
          this.form.deviceId = newDevice.id || null
          console.log('Device ID:', this.form.deviceId)
          Object.assign(this.form, {
            serialNumber: newDevice.serialNumber || '',
            deviceName: newDevice.deviceName || '',
            model: newDevice.model || '',
            categoryId: newDevice.category?.id || null,
            subcategoryId: newDevice.subcategory?.id || null,
            userId: newDevice.user?.id || null,
            departmentId: newDevice.department?.id || null,
            locationId: newDevice.location?.id || null,
            purchaseDate: newDevice.purchaseDate || '',
            price: newDevice.price || 0,
            status: newDevice.status || '',
            purchaseStatus: newDevice.purchaseStatus || '',
            inactiveDate: newDevice.disDate || null,
            details: newDevice.details || '',
          })

          if (this.form.categoryId) {
            this.fetchSubcategories()
          }
          if (newDevice.id) {
            this.fetchWarranties()
            this.fetchDeviceHardwares()
            this.fetchImages()
          }
        }
      },
    },
  },
  mounted() {
    if (this.device && this.device.id) {
      this.fetchImages()
    } else {
      console.warn('Device is not set or has no ID.')
    }
  },

  created() {
    this.categoriesOptions = this.categories.map(c => ({ value: c.id, text: c.name }))
    this.usersOptions = [...this.users.map(u => ({ value: u.id, text: u.username })), { value: null, text: this.$t('other') }]
    this.locationsOptions = this.locations.map(l => ({ value: l.id, text: l.locationName }))
    this.departmentsOptions = this.departments.map(d => ({ value: d.id, text: d.dept }))
  },
  methods: {
    async fetchSubcategories() {
      if (this.form.categoryId) {
        this.isLoading.SubCate = true
        try {
          const response = await apiMst.get(`/subdevice/${this.form.categoryId}`)
          if (Array.isArray(response.data)) {
            this.subcategoriesOptions = response.data.map(sc => ({ value: sc.id, text: sc.name }))
          } else {
            console.error('Expected an array but got:', response.data)
            this.subcategoriesOptions = []
          }
        } catch (error) {
          console.error('Error fetching subcategories:', error)
          this.subcategoriesOptions = []
        } finally {
          this.isLoading.SubCate = false
        }
      }
    },
    async submitForm() {
      console.log('Before submitting form:', this.form)
      try {
        const deviceId = await this.savedevice()
        console.log('Device ID:', deviceId)

        if (!deviceId) {
          throw new Error('No device ID returned. Cannot upload files.')
        }

        this.device.id = deviceId

        await this.uploadFiles(`/uploads/${deviceId}`, this.selectedFiles)

        await this.submitFormDeviceHistory()

        this.$emit('device-saved')
        this.$emit('close')
      } catch (error) {
        console.error('Error saving data:', error)
        alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล')
      }
    },

    async savedevice() {
      const deviceData = {
        serialNumber: this.form.serialNumber,
        deviceName: this.form.deviceName,
        model: this.form.model,
        category: { id: this.form.categoryId },
        subcategory: { id: this.form.subcategoryId },
        user: this.form.userId ? { id: this.form.userId } : null,
        department: { id: this.form.departmentId },
        location: { id: this.form.locationId },
        purchaseDate: this.form.purchaseDate,
        price: this.form.price,
        status: this.form.status,
        purchaseStatus: this.form.purchaseStatus,
        disDate: this.isInactive ? this.form.inactiveDate : null,
        details: this.form.details,
      }

      try {
        let response
        if (this.device && this.device.id) {
          response = await apiMst.put(`/deviceListUpdate/${this.device.id}`, deviceData)
        } else {
          response = await apiMst.post('/deviceListAdd', deviceData)
        }

        console.log('API Response:', response.data)

        if (response.data && response.data.id) {
          return response.data.id
        }
        throw new Error('Device ID not returned from API')
      } catch (error) {
        console.error('Error saving device:', error.response ? error.response.data : error.message)
        throw error
      }
    },

    handleCategoryChange(event) {
      this.fetchSubcategories()
    },
    handleFileChange(event) {
      this.selectedFiles = Array.from(event.target.files)
    },
    async uploadFiles(url, files) {
      if (files.length === 0) return
      const formData = new FormData()
      files.forEach(file => formData.append('files', file))
      formData.append('deviceId', this.device.id || '')

      try {
        const response = await apiMst.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        return response.data
      } catch (error) {
        console.error('Error uploading files:', error)
        alert('เกิดข้อผิดพลาดในการอัปโหลดไฟล์')
        throw error
      }
    },
    showImage(url) {
      this.selectedImage = url
      this.imageModalShow = true
    },

    handleImageClose() {
      this.selectedImage = ''
    },

    async onUserChanged() {
      this.showDeviceHistoryModal = true
    },

    handleModalOk() {
      this.showDeviceHistoryModal = false
    },

    async submitFormDeviceHistory() {
      // เช็คว่ามีค่าของ note หรือไม่
      if (!this.history.note) {
        console.error('Note is required.')
        return
      }
      if (!this.device.id || !this.form.userId) {
        console.warn('Device ID or User ID is missing.')
        return
      }

      try {
        // ส่งข้อมูล note ผ่าน query string
        const response = await apiAdmin.post(`/deviceUseHistory/${this.device.id}/${this.form.userId}?note=${encodeURIComponent(this.history.note)}`)

        console.log('User change success:', response.data)
        this.history.note = '' // รีเซ็ตค่า note
      } catch (error) {
        console.error('Error changing user:', error)
      }
    },

    async fetchImages() {
      const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'https://mns.gelogs.co.th/api/'
      this.isLoading.Images = true
      try {
        const response = await apiMst.get(`/images/${this.device.id}`)
        if (response.status === 200 && response.data && response.data.length > 0) {
          this.imageUrls = response.data.map(filePath => `${apiBaseUrl}${filePath}`)
        } else {
          this.imageUrls = []
        }
      } catch (error) {
        this.imageUrls = []
        if (error.response && error.response.status !== 404) {
          console.error('Error fetching images:', error)
        }
      } finally {
        this.isLoading.Images = false
      }
    },

    calculateTotalDays(years, months, days) {
      return (years * 365) + (months * 30) + days
    },

    formatWarrantyDay(totalDays) {
      const years = Math.floor(totalDays / 365)
      const months = Math.floor((totalDays % 365) / 30)
      const days = totalDays % 30

      let result = ''
      if (years > 0) {
        result += `${years}  `
      }
      if (months > 0) {
        result += `${months} ${this.$t('month')} `
      }
      if (years === 0 && months === 0 && days > 0) {
        result += `${days} ${this.$t('day')}`
      }

      return result.trim() || this.$t('zeroday')
    },

    formatWarrantyExp(expDate) {
      const exp = new Date(expDate)
      const now = new Date()

      now.setHours(0, 0, 0, 0)
      exp.setHours(0, 0, 0, 0)

      const diffTime = exp - now
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
      const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

      let color = 'black'
      if (diffDays < 0) {
        color = 'red'
      } else if (diffDays <= 30) {
        color = 'orange'
      }

      let timeLeft = ''
      if (diffDays < 0) {
        timeLeft = this.$t('exp')
      } else {
        timeLeft = this.$t('after')
        if (diffDays > 0) {
          timeLeft += `${diffDays} ${this.$t('day')} `
        } else if (diffHours > 0) {
          timeLeft += `${diffHours} ${this.$t('hour')}`
        } else {
          timeLeft += this.$t('inday')
        }
      }

      const formattedDate = `${exp.getDate()}/${exp.getMonth() + 1}/${exp.getFullYear() + 543}`

      return {
        text: `${formattedDate} (${timeLeft.trim()})`,
        color,
      }
    },
    handleStatusChange() {
      if (this.isInactive) {
        this.form.inactiveDate = null
      }
    },
    checkLength() {
      if (this.form.details.length >= 150) {
        this.lengthExceeded = true
        this.form.details = this.form.details.slice(0, 150)
      } else {
        this.lengthExceeded = false
      }
    },
    checkwarrantyLength() {
      if (this.warranty.warrantyNote.length >= 100) {
        this.lengthwarrantyExceeded = true
        this.warranty.warrantyNote = this.warranty.warrantyNote.slice(0, 100)
      } else {
        this.lengthwarrantyExceeded = false
      }
    },
    checkdevicehdLength() {
      if (this.deviceHardware.details.length >= 100) {
        this.lengthdevicehdExceeded = true
        this.deviceHardware.details = this.deviceHardware.details.slice(0, 100)
      } else {
        this.lengthdevicehdExceeded = false
      }
    },

    updateDisplay() {
      const { warrantyYear, warrantyMonth, warrantyDay } = this.warranty
      let displayText = ''

      if (warrantyYear > 0) {
        displayText += `${warrantyYear} ${this.$t('year')} `
      }
      if (warrantyMonth > 0) {
        displayText += `${warrantyMonth} ${this.$t('month')} `
      }
      if (warrantyDay > 0) {
        displayText += `${warrantyDay} ${this.$t('day')} `
      }

      this.displayWarranty = displayText.trim() || this.$t('nowarranty')
    },

    // warranty
    async saveWarranty() {
      if (!this.warranty.vendor) {
        console.error('กรุณากรอกข้อมูลประกันให้ครบถ้วน')
        return
      }

      const totalDays = this.calculateTotalDays(this.warranty.warrantyYear || 0, this.warranty.warrantyMonth || 0, this.warranty.warrantyDay || 0)

      const warrantyPayload = {
        device: { id: this.device.id },
        vendor: this.warranty.vendor,
        warrantyDay: totalDays,
        warrantyExp: this.warranty.warrantyExp,
        warrantyNote: this.warranty.warrantyNote,
      }

      const endpoint = this.warranty.id ? `/warrantyUpdate/${this.warranty.id}` : '/warrantyAdd'

      try {
        const response = await apiMst[this.warranty.id ? 'put' : 'post'](endpoint, warrantyPayload)
        if (response.status === 200 || response.status === 201) {
          console.log('Warranty saved successfully:', response.data)
          this.warranty.id = response.data.id // อัปเดต warranty ID หากมีการเพิ่ม
          this.showwarrantyModal = false
          await this.fetchWarranties() // อัปเดตรายการ warranty
          this.clearForm()
        }
      } catch (error) {
        console.error('Error saving warranty:', error)
      }
    },
    clearForm() {
      this.warranty = {
        vendor: '',
        warrantyDay: '',
        warrantyExp: null,
        warrantyNote: '',
        warrantyYear: 0,
        warrantyMonth: 0,
      }
    },
    editWarranty(item) {
      this.warranty = { ...item }
      this.showwarrantyModal = true
    },

    deleteWarrantyModel(warrantyid) {
      this.warrantyIdToDelete = warrantyid
      this.showConfirmationModalwarranty = true
    },

    deleteWarranty() {
      apiMst.delete(`/warrantyDel/${this.warrantyIdToDelete}`)
        .then(() => {
          this.fetchWarranties()
          this.showConfirmationModalwarranty = false
        })
        .catch(error => {
          console.error('Error deleting warranty:', error)
          this.showConfirmationModalwarranty = false
        })
    },

    async fetchWarranties() {
      this.isLoading.warranty = true
      try {
        const response = await apiMst.get(`/warrantyByDeviceId/${this.device.id}`)
        if (response.status === 200) {
          this.warrantyList = response.data
          console.log('Warranty List:', this.warrantyList)
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          console.warn('No warranties found for this device.')
          this.warrantyList = []
        } else {
          console.error('Error fetching warranties:', error)
        }
      } finally {
        this.isLoading.warranty = false
      }
    },

    // devicehardware
    editDeviceHardware(item) {
      this.deviceHardware = { ...item }
      this.showDeviceModal = true
    },
    async saveDeviceHardware() {
      // ตรวจสอบข้อมูลที่จำเป็น
      if (!this.deviceHardware.cpu || !this.deviceHardware.ram || !this.deviceHardware.display) {
        console.error('กรุณากรอกข้อมูลอุปกรณ์ให้ครบถ้วน')
        return
      }

      const deviceHardwarePayload = {
        device: { id: this.device.id },
        cpu: this.deviceHardware.cpu,
        ram: this.deviceHardware.ram,
        display: this.deviceHardware.display,
        harddisk: this.deviceHardware.harddisk,
        ssd: this.deviceHardware.ssd,
        ip: this.deviceHardware.ip,
        macAdd: this.deviceHardware.macAdd,
        keyboard: this.deviceHardware.keyboard,
        mouse: this.deviceHardware.mouse,
        ups: this.deviceHardware.ups,
        details: this.deviceHardware.details,
      }

      const endpoint = this.deviceHardware.id ? `/devicehardwareUpdate/${this.deviceHardware.id}` : '/devicehardwareAdd'

      try {
        const response = await apiMst[this.deviceHardware.id ? 'put' : 'post'](endpoint, deviceHardwarePayload)

        if (response.status === 200 || response.status === 201) {
          console.log('อุปกรณ์บันทึกสำเร็จ:', response.data)
          this.deviceHardware.id = response.data.id
          this.showDeviceModal = false
          await this.fetchDeviceHardwares()
          this.clearFormdevicehardware()
        }
      } catch (error) {
        console.error('Error saving device hardware:', error)
      }
    },

    async fetchDeviceHardwares() {
      try {
        const response = await apiMst.get(`/devicehardwareByDeviceId/${this.device.id}`)
        if (response.status === 200) {
          this.deviceHardwareList = response.data
          console.log('Device Hardware List:', this.deviceHardwareList)
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          console.warn('No hardwares found for this device.')
          this.deviceHardwareList = []
        } else {
          console.error('Error fetching device hardwares:', error)
        }
      }
    },

    deleteDeviceHardwareModel(hardwareId) {
      this.hardwareIdToDelete = hardwareId
      this.showConfirmationModalhardware = true
    },

    deleteDeviceHardware() {
      apiMst.delete(`/devicehardwareDel/${this.hardwareIdToDelete}`)
        .then(() => {
          this.fetchDeviceHardwares()
          this.showConfirmationModalhardware = false
        })
        .catch(error => {
          console.error('Error deleting spare:', error)
          this.showConfirmationModalhardware = false
        })
    },

    clearFormdevicehardware() {
      this.deviceHardware = {
        id: null,
        cpu: '',
        ram: '',
        display: '',
        harddisk: '',
        ssd: '',
        ip: '',
        macAdd: '',
        keyboard: '',
        mouse: '',
        ups: '',
        details: '',
        device: null, // หรือ null ตามความเหมาะสม
      }
    },
    checkhistoryDetailsLength() {
      if (this.history.note.length >= 30) {
        this.lengthhistoryDetailsExceeded = true
        this.history.note = this.history.note.slice(0, 30)
      } else {
        this.lengthhistoryDetailsExceeded = false
      }
    },

    limitInputcost(event) {
      const maxLength = 15

      const inputValue = this.form.price.toString()

      if (inputValue.length >= maxLength && event.key !== 'Backspace') {
        event.preventDefault()
      }

      if (inputValue.indexOf('.') !== -1) {
        const decimalPart = inputValue.split('.')[1]
        if (decimalPart && decimalPart.length >= 2 && event.key !== 'Backspace') {
          event.preventDefault()
        }
      }
    },

  },
}
</script>

<style scoped>
.modal-content {
  padding: 20px;
}

.form-section {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f9f9f9;
}
.form-section {
  flex: 1; /* ทำให้แต่ละส่วนขยายเต็มที่ */
  min-width: 300px; /* กำหนดความกว้างขั้นต่ำ */
  max-width: 100%; /* กำหนดความกว้างสูงสุด */
  box-sizing: border-box; /* ให้ความสูงรวมของ box รวม padding และ border */
  white-space: nowrap;
}

.section-title {
  background-color: #4a6a8c;
  color: white;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 15px;
}

.b-form-group {
  margin-bottom: 15px;
}
.form-row .b-form-group {
  flex: 1;
}

.img-thumbnail {
  max-width: 200px;
  max-height: 200px;
  cursor: pointer;
  transition: transform 0.2s;
}

.img-thumbnail:hover {
  transform: scale(1.05);
}

.form-row {
  display: flex;
  gap: 20px;
}

.input-box{
  width: 200px;
  height: 40px;
}

.input-box-1{
  width: 300px;
  height: 40px;
}

.input-text{
  width: 400px;
  height: 100px;
  resize: none;
}

.form-buttons {
  margin-top: 15px;
  display: flex;
  gap: 20px;
  justify-content: flex-end;
}

.input-texthistory{
  width: 400px;
  height: 60px;
  resize: none;
}

.input-image{
  width: 400px;
  height: 50px;
}

.input-text{
  width: 400px;
  height: 100px;
  resize: none;
}

.btn-space{
  margin: 3px;
}

.input-textcancel{
  width: 400px;
  height: 60px;
  resize: none;
}

.form-container {
  display: flex;
  justify-content: space-between; /* หรือ flex-start ตามที่ต้องการ */
  gap: 20px; /* ระยะห่างระหว่างสองกล่อง */
  flex-wrap: wrap /* ให้กล่องย่อไปตามพื้นที่ที่มี */
}

@media (max-width: 768px) {
  .form-container {
    flex-direction: column; /* แสดงในแนวตั้งบนหน้าจอเล็ก */
  }
  .input-text{
  width: 200px;
  height: 100px;
  resize: none;
  }
  .input-image{
  width: 300px;
  height: 50px;
}
.input-texthistory{
  width: 300px;
  height: 100px;
  resize: none;
}
}

@media (max-width: 360px) {
  .form-container {
    flex-direction: column; /* แสดงในแนวตั้งบนหน้าจอเล็ก */
  }
  .input-text{
  width: 200px;
  height: 100px;
  resize: none;
  }
  .input-image{
  width: 250px;
  height: 50px;
}
.input-texthistory{
  width: 250px;
  height: 100px;
  resize: none;
}
}
</style>
